import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';

import { storage } from '@/modules/firebase';

import { fileClient, type RecordFile, type RecordFileType } from './FileClient';

export const MAX_RECORD_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const ALLOWED_RECORD_FILE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'pdf', 'xlsx', 'csv', 'txt']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);
const SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xls', 'csv']);
const TEXT_EXTENSIONS = new Set(['txt']);

export type RecordFilePreviewKind = 'image' | 'pdf' | 'spreadsheet' | 'text' | 'file';

export interface RecordFilePreviewSource {
  displayName: string;
  originalName: string;
  mimeType: string;
  extension: string;
  sizeBytes: number;
  storagePath: string;
  version: number;
  updatedAt?: number;
  status?: string;
  deleted?: boolean;
}

export interface UploadRecordFilesOptions {
  recordType: RecordFileType;
  recordId: string;
  files: File[];
  targetFile?: RecordFile | null;
  onProgress?: (progress: {
    fileName: string;
    uploadIndex: number;
    uploadCount: number;
    filePercent: number;
    percent: number;
  }) => void;
}

export function getRecordFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.trim().toLowerCase() || '';
}

export function validateLocalRecordFile(file: File): string | null {
  const extension = getRecordFileExtension(file.name);
  if (!ALLOWED_RECORD_FILE_EXTENSIONS.has(extension)) {
    return `${file.name} is not a supported file type`;
  }

  if (file.size > MAX_RECORD_FILE_SIZE_BYTES) {
    return `${file.name} exceeds the 10 MB limit`;
  }

  return null;
}

export function buildRecordFileDuplicateKey(fileName: string, sizeBytes: number): string {
  return `${fileName.trim().toLowerCase()}::${sizeBytes}`;
}

export function findDuplicateRecordFileUpload(
  fileList: File[] | FileList,
  existingFiles: Array<Pick<RecordFilePreviewSource, 'displayName' | 'originalName' | 'sizeBytes' | 'status' | 'deleted'>>
): string | null {
  const existingKeys = new Set(
    existingFiles
      .filter(file => file.deleted !== true && file.status !== 'deleted')
      .map(file => buildRecordFileDuplicateKey(file.originalName || file.displayName, file.sizeBytes))
  );
  const batchKeys = new Set<string>();

  for (const file of Array.from(fileList)) {
    const duplicateKey = buildRecordFileDuplicateKey(file.name, file.size);
    if (existingKeys.has(duplicateKey) || batchKeys.has(duplicateKey)) {
      return file.name;
    }

    batchKeys.add(duplicateKey);
  }

  return null;
}

export function getRecordFilePreviewKind(file: Pick<RecordFilePreviewSource, 'displayName' | 'originalName' | 'mimeType' | 'extension'>): RecordFilePreviewKind {
  const extension = getRecordFileExtension(file.displayName || file.originalName || file.extension);

  if (file.mimeType.startsWith('image/') || IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }

  if (extension === 'pdf') {
    return 'pdf';
  }

  if (SPREADSHEET_EXTENSIONS.has(extension)) {
    return 'spreadsheet';
  }

  if (TEXT_EXTENSIONS.has(extension)) {
    return 'text';
  }

  return 'file';
}

export function getRecordFilePreviewLabel(file: Pick<RecordFilePreviewSource, 'displayName' | 'originalName' | 'mimeType' | 'extension'>): string {
  const previewKind = getRecordFilePreviewKind(file);
  if (previewKind === 'image') {
    return 'IMG';
  }

  if (previewKind === 'pdf') {
    return 'PDF';
  }

  if (previewKind === 'spreadsheet') {
    return file.extension.toUpperCase().slice(0, 4) || 'XLS';
  }

  if (previewKind === 'text') {
    return file.extension.toUpperCase().slice(0, 4) || 'TXT';
  }

  return file.extension.toUpperCase().slice(0, 4) || 'FILE';
}

export function buildRecordFileThumbnailSignature(file: Pick<RecordFilePreviewSource, 'storagePath' | 'version' | 'updatedAt'>): string {
  return `${file.storagePath}|${file.version}|${file.updatedAt || 0}`;
}

export async function uploadRecordFiles(options: UploadRecordFilesOptions): Promise<RecordFile[]> {
  const uploadedFiles: RecordFile[] = [];
  const { recordType, recordId, targetFile, onProgress } = options;
  const uploadFiles = options.files;

  for (const [index, file] of uploadFiles.entries()) {
    const payload = {
      recordType,
      recordId,
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      displayName: targetFile?.displayName,
      fileId: targetFile?.id
    };

    const session = targetFile
      ? await fileClient.replaceFileVersion({ ...payload, fileId: targetFile.id })
      : await fileClient.createUploadSession(payload);

    await new Promise<void>((resolve, reject) => {
      const task = uploadBytesResumable(storageRef(storage, session.uploadPath), file, {
        contentType: file.type || session.file.mimeType
      });

      task.on('state_changed', snapshot => {
        const fileProgress = snapshot.totalBytes > 0
          ? snapshot.bytesTransferred / snapshot.totalBytes
          : 0;
        const overallProgress = ((index + fileProgress) / uploadFiles.length) * 100;

        onProgress?.({
          fileName: file.name,
          uploadIndex: index,
          uploadCount: uploadFiles.length,
          filePercent: Math.max(0, Math.min(100, Math.round(fileProgress * 100))),
          percent: Math.max(0, Math.min(100, Math.round(overallProgress)))
        });
      }, reject, () => resolve());
    });

    uploadedFiles.push(await fileClient.finalizeFileUpload(recordType, recordId, session.file.id));
  }

  return uploadedFiles;
}